import React, { forwardRef } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { fr } from "date-fns/locale/fr";
import { enUS } from "date-fns/locale/en-US";
import { useTranslation } from "react-i18next";
import { Calendar, Clock } from "lucide-react";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker.css";

registerLocale("fr", fr);
registerLocale("en", enUS);
registerLocale("ht", fr);

const buildTrigger = ({ placeholder, icon, testId, disabled }) =>
    forwardRef(({ value, onClick }, ref) => (
        <button
            type="button"
            ref={ref}
            onClick={onClick}
            disabled={disabled}
            data-testid={testId}
            className="input-modern !flex !items-center !justify-between !text-left !py-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
            <span className={value ? "text-foreground" : "text-muted-foreground"}>
                {value || placeholder || ""}
            </span>
            <span className="text-muted-foreground opacity-60 ml-2">{icon}</span>
        </button>
    ));

const parseDateStr = (s) => {
    if (!s) return null;
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};
const formatDateStr = (d) => {
    if (!d) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const parseTimeStr = (s) => {
    if (!s) return null;
    const [h, m] = s.split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
};
const formatTimeStr = (d) => {
    if (!d) return "";
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

export const CustomDatePicker = ({ value, onChange, placeholder, testId, disabled = false, minDate, maxDate }) => {
    const { i18n, t } = useTranslation();
    const locale = i18n.language === "fr" ? "fr" : i18n.language === "ht" ? "ht" : "en";
    const Trigger = React.useMemo(
        () =>
            buildTrigger({
                placeholder: placeholder || t("common.choose"),
                icon: <Calendar className="h-4 w-4" />,
                testId,
                disabled,
            }),
        [placeholder, testId, disabled, t],
    );
    return (
        <DatePicker
            selected={parseDateStr(value)}
            onChange={(d) => onChange(formatDateStr(d))}
            dateFormat="MMM d, yyyy"
            locale={locale}
            disabled={disabled}
            minDate={minDate}
            maxDate={maxDate}
            popperPlacement="bottom-start"
            calendarClassName="yvt-datepicker"
            customInput={<Trigger />}
        />
    );
};

export const CustomTimePicker = ({ value, onChange, placeholder, testId, disabled = false, intervals = 15 }) => {
    const { i18n } = useTranslation();
    const locale = i18n.language === "fr" ? "fr" : i18n.language === "ht" ? "ht" : "en";
    const Trigger = React.useMemo(
        () =>
            buildTrigger({
                placeholder: placeholder || "00:00",
                icon: <Clock className="h-4 w-4" />,
                testId,
                disabled,
            }),
        [placeholder, testId, disabled],
    );
    return (
        <DatePicker
            selected={parseTimeStr(value)}
            onChange={(d) => onChange(formatTimeStr(d))}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={intervals}
            timeCaption="Time"
            dateFormat="h:mm aa"
            locale={locale}
            disabled={disabled}
            popperPlacement="bottom-start"
            calendarClassName="yvt-datepicker"
            customInput={<Trigger />}
        />
    );
};
